import React, { useState, useMemo, useEffect } from 'react';
import { Calendar, dateFnsLocalizer, Views } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { supabase } from '../../supabaseClient';
import { fetchCalendarClassWithProfiles } from '../../database/databaseClassProfile';
import { addDays } from 'date-fns';
import esES from 'date-fns/locale/es';

import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';

import ReserveModal from './ReserveModal';

const isMobile = window.innerWidth < 640;
const calendarView = isMobile ? Views.DAY : Views.WORK_WEEK;

// Setup locales
const locales = { es: esES, 'es-ES': esES };
const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
    getDay,
    locales,
});

const DragAndDropCalendar = withDragAndDrop(Calendar);

const makeDraftEvent = (slot) => ({
    id: '__draft__',
    title: '(nueva clase)',
    start: slot.start,
    end: new Date(slot.start.getTime() + 60 * 60 * 1000),
    allDay: false,
});

export default function AdminCalendar() {
    const [modalOpenMorning, setModalOpenMorning] = useState(false);
    const [modalOpenAfter, setModalOpenAfter] = useState(false);
    const [slotInfoMorning, setSlotInfoMorning] = useState(null);
    const [draftMorning, setDraftMorning] = useState(null);
    const [slotInfoAfter, setSlotInfoAfter] = useState(null);
    const [draftAfter, setDraftAfter] = useState(null);
    const [realEvents, setRealEvents] = useState([]);
    const [currentDate, setCurrentDate] = useState(new Date());
    const [calSettings, setCalSettings] = useState({ minMorning: 8, maxMorning: 13, minAfternoon: 17, maxAfternoon: 21 });

    const goToday = () => setCurrentDate(new Date());
    const goPrev = () => {
        let next = addDays(currentDate, isMobile ? -1 : -7);
        while (isMobile && isWeekend(next)) {
            next = addDays(next, -1);
        }
        setCurrentDate(next);
    };

    const goNext = () => {
        let next = addDays(currentDate, isMobile ? 1 : 7);
        while (isMobile && isWeekend(next)) {
            next = addDays(next, 1);
        }
        setCurrentDate(next);
    };

    const isWeekend = (date) => {
        const day = date.getDay(); // 0 = domingo, 6 = sábado
        return day === 0 || day === 6;
    };

    // 🎨 Color styling for each event
    const eventPropGetter = (event) => {
        const style = {
            backgroundColor: event.color || '#a855f7',
            color: 'white',
            borderRadius: '4px',
            border: 'none',
            padding: '2px 4px',
            fontSize: '0.85rem',
        };
        if (event.id === '__draft__') style.opacity = 0.8;
        return { style };
    };
    const fetchEvents = async () => {
        const data = await fetchCalendarClassWithProfiles();
        const formatted = (data || []).map((ev) => {
            // Build "Pilates y movement 8/9" title
            const titleWithCounts = ev.maxattendees ? `${ev.title} ${ev.confirmed_count || 0}/${ev.maxattendees}` : ev.title;

            return {
                id: ev.calendarclass_id,
                class: ev.class_id,
                title: titleWithCounts,
                titleRaw: ev.title,
                start: new Date(`${ev.date}T${ev.starttime}`),
                end: new Date(`${ev.date}T${ev.endtime}`),
                date: ev.date,
                color: ev.color ?? '#a855f7',
                comment: ev.comment || '',
                confirmed_count: ev.confirmed_count,
                maxattendees: ev.maxattendees,
            };
        });

        setRealEvents(formatted);
    };

    useEffect(() => {
        fetchEvents();
        supabase.from('settings').select('calendar_min_morning,calendar_max_morning,calendar_min_afternoon,calendar_max_afternoon').eq('id', 1).maybeSingle().then(({ data }) => {
            if (data) setCalSettings({
                minMorning: data.calendar_min_morning ?? 8,
                maxMorning: data.calendar_max_morning ?? 13,
                minAfternoon: data.calendar_min_afternoon ?? 17,
                maxAfternoon: data.calendar_max_afternoon ?? 21,
            });
        });
    }, []);

    const today = new Date();
    const atTime = (h, m = 0) => new Date(today.getFullYear(), today.getMonth(), today.getDate(), h, m);

    const eventsMorning = useMemo(() => {
        return [...realEvents.filter((e) => e.start.getHours() < 13), ...(draftMorning ? [draftMorning] : [])];
    }, [realEvents, draftMorning]);

    const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

    const mobileDayLabel = useMemo(() => {
        // e.g. "lunes 3"
        const raw = format(currentDate, 'EEEE d', { locale: esES });
        return capitalize(raw);
    }, [currentDate]);

    const eventsAfternoon = useMemo(() => {
        return [...realEvents.filter((e) => e.start.getHours() >= 13), ...(draftAfter ? [draftAfter] : [])];
    }, [realEvents, draftAfter]);

    // 📅 Click to create event
    const handleSelectSlotMorning = (info) => {
        console.log('Selected slot (morning):', info);
        const adjusted = { ...info, end: new Date(info.start.getTime() + 60 * 60 * 1000) };
        setDraftMorning(makeDraftEvent(adjusted));
        setSlotInfoMorning(adjusted);
        setModalOpenMorning(true);
    };

    const handleSelectSlotAfter = (info) => {
        const adjusted = { ...info, end: new Date(info.start.getTime() + 60 * 60 * 1000) };
        setDraftAfter(makeDraftEvent(adjusted));
        setSlotInfoAfter(adjusted);
        setModalOpenAfter(true);
    };

    const handleEditEvent = (event) => {
        const isMorning = event.start.getHours() < 13;
        const draft = {
            id: event.id,
            class: event.class,
            title: event.title,
            start: new Date(event.start),
            end: new Date(event.end),
            allDay: false,
            comment: event.comment || '',
        };
        const slot = {
            class: draft.class,
            start: draft.start,
            end: draft.end,
            date: event.date,
            id: draft.id,
            comment: draft.comment || '',
            title: draft.titleRaw || '',
            color: event.color || '#a855f7',
            confirmed_count: event.confirmed_count,
            maxattendees: event.maxattendees,
        };
        if (isMorning) {
            setDraftMorning(draft);
            setSlotInfoMorning(slot);
            setModalOpenMorning(true);
        } else {
            setDraftAfter(draft);
            setSlotInfoAfter(slot);
            setModalOpenAfter(true);
        }
    };

    const handleEventDrop = ({ event, start, end }) => {
        const ok = window.confirm(`¿Quieres mover la clase "${event.title}" a:\n\n${format(start, 'PPPPp')} - ${format(end, 'p')}?`);
        if (!ok) return;

        (async () => {
            const formattedDate = format(start, 'yyyy-MM-dd');
            const formattedStart = format(start, 'HH:mm:ss'); // TIME wants seconds
            const formattedEnd = format(end, 'HH:mm:ss');

            // sanity: can we SELECT this row by id?
            const { data: exists, error: selErr } = await supabase.from('calendarclass').select('id').eq('id', event.id).maybeSingle();

            if (selErr) {
                console.error('SELECT by id failed (RLS?):', selErr);
                alert('No puedo leer el evento (revisa permisos).');
                return;
            }
            if (!exists) {
                console.warn('Row not found. Bad id?', event.id);
                alert('No se encontró el evento a actualizar.');
                return;
            }

            const { data: updatedRows, error: updErr } = await supabase
                .from('calendarclass')
                .update({
                    date: formattedDate,
                    starttime: formattedStart,
                    endtime: formattedEnd,
                })
                .eq('id', event.id)
                .select('id'); // returns an array of updated rows

            if (updErr) {
                console.error('UPDATE error:', updErr);
                alert('Error al mover el evento: ' + updErr.message);
                return;
            }

            console.log('Updated count:', updatedRows?.length || 0);
            if (!updatedRows || updatedRows.length === 0) {
                alert('No se pudo actualizar (RLS WITH CHECK probablemente bloquea).');
                return;
            }

            fetchEvents();
        })();
    };

    const handleCloseMorning = () => {
        setModalOpenMorning(false);
        setSlotInfoMorning(null);
        setDraftMorning(null);
    };

    const handleCloseAfter = () => {
        setModalOpenAfter(false);
        setSlotInfoAfter(null);
        setDraftAfter(null);
    };

    const weekLabel = useMemo(() => {
        const start = startOfWeek(currentDate, { weekStartsOn: 1 });
        const end = addDays(start, 4); // Friday for work week
        const s = format(start, "d 'de' LLLL", { locale: esES });
        const e = format(end, "d 'de' LLLL", { locale: esES });
        return `Semana del ${s} al ${e}`;
    }, [currentDate]);

    return (
        <>
            <style>
                {`.rbc-allday-cell {
                    display: none !important;
                }
                .rbc-time-view,
                .rbc-time-header,
                .rbc-time-header-content,
                .rbc-time-content,
                .rbc-time-gutter,
                .rbc-day-slot,
                .rbc-month-view,
                .rbc-month-row,
                .rbc-day-bg,
                .rbc-off-range-bg {
                    background-color: #F6EFE6;
                    border-color: #E3D3BE !important;
                }
                .rbc-header {
                    background-color: #F6EFE6;
                    color: #2B1D17;
                    border-color: #E3D3BE !important;
                }
                .rbc-today {
                    background-color: rgba(196, 80, 42, 0.07);
                }
                .rbc-timeslot-group,
                .rbc-time-slot {
                    border-color: #E3D3BE !important;
                }
                .rbc-current-time-indicator {
                    background-color: #C4502A;
                }`}
            </style>

            <div className='flex w-full md:w-auto gap-2'>
                <button
                    onClick={goToday}
                    className='flex-1 md:flex-none px-4 py-3 md:py-2 rounded-lg bg-terracotta-light text-ink font-medium shadow-sm
               hover:bg-terracotta hover:text-cream active:bg-terracotta-dark transition-colors text-sm md:text-base'
                >
                    Hoy
                </button>
                <button
                    onClick={goPrev}
                    className='flex-1 md:flex-none px-4 py-3 md:py-2 rounded-lg bg-sand text-ink font-medium shadow-sm
               hover:bg-sand active:bg-cream-dark transition-colors text-sm md:text-base'
                >
                    Atrás
                </button>
                <button
                    onClick={goNext}
                    className='flex-1 md:flex-none px-4 py-3 md:py-2 rounded-lg bg-terracotta-light text-ink font-medium shadow-sm
               hover:bg-terracotta hover:text-cream active:bg-terracotta-dark transition-colors text-sm md:text-base'
                >
                    Siguiente
                </button>
            </div>

            <div className='text-center px-3 py-1 rounded-md bg-sand border border-terracotta/20 text-sm text-ink/80 shadow-sm' translate='no'>
                {weekLabel}
            </div>
            <div className='text-center px-3 py-1 rounded-md bg-sand border border-terracotta/20 text-sm text-ink/80 shadow-sm md:hidden' translate='no'>
                {mobileDayLabel}
            </div>

            <section className='mr-4' translate='no'>
                <h2 className='text-xl text-center font-sans font-semibold text-terracotta mb-2'>HORARIO DE MAÑANA</h2>
                <DragAndDropCalendar
                    className='rbc-no-top-gutter'
                    localizer={localizer}
                    selectable
                    showAllDaySlot={false}
                    toolbar={false}
                    date={currentDate}
                    onNavigate={(date) => {
                        let next = date;
                        while (isMobile && isWeekend(next)) {
                            next = addDays(next, 1);
                        }
                        setCurrentDate(next);
                    }}
                    defaultView={calendarView}
                    views={isMobile ? [Views.DAY] : [Views.WORK_WEEK]}
                    onSelectSlot={handleSelectSlotMorning}
                    events={eventsMorning}
                    eventPropGetter={eventPropGetter}
                    onSelectEvent={handleEditEvent}
                    onEventDrop={handleEventDrop}
                    draggableAccessor={() => true}
                    step={60}
                    longPressThreshold={1}
                    timeslots={1}
                    min={atTime(calSettings.minMorning, 0)}
                    max={atTime(calSettings.maxMorning, 0)}
                    daysOfWeek={[1, 2, 3, 4, 5]}
                    culture='es'
                    formats={{
                        timeGutterFormat: 'HH:mm',
                        dayHeaderFormat: (date, culture, localizer) => {
                            const day = localizer.format(date, 'EEEE', culture); // lunes
                            const dayNum = localizer.format(date, 'd', culture); // 15
                            const month = localizer.format(date, 'LLLL', culture); // noviembre (full)
                            return `${day.charAt(0).toUpperCase() + day.slice(1)} ${dayNum} ${month}`;
                        },
                        dayRangeHeaderFormat: ({ start, end }, culture, localizer) => {
                            const s = localizer.format(start, "d 'de' LLLL", culture);
                            const e = localizer.format(end, "d 'de' LLLL", culture);
                            return `Semana del ${s} al ${e}`;
                        },
                    }}
                />
            </section>
            {/* ────────── Add break ────────── */}
            <div className='border-t border-sand my-4' />
            <section className='mr-4' translate='no'>
                <h2 className='text-xl text-center font-sans font-semibold text-terracotta mb-2'>HORARIO DE TARDE</h2>
                <DragAndDropCalendar
                    localizer={localizer}
                    selectable
                    toolbar={false}
                    date={currentDate}
                    onNavigate={(date) => {
                        let next = date;
                        while (isMobile && isWeekend(next)) {
                            next = addDays(next, 1);
                        }
                        setCurrentDate(next);
                    }}
                    defaultView={calendarView}
                    views={isMobile ? [Views.DAY] : [Views.WORK_WEEK]}
                    onSelectSlot={handleSelectSlotAfter}
                    events={eventsAfternoon}
                    eventPropGetter={eventPropGetter}
                    onSelectEvent={handleEditEvent}
                    onEventDrop={handleEventDrop}
                    draggableAccessor={() => true}
                    step={60}
                    longPressThreshold={1}
                    timeslots={1}
                    min={atTime(calSettings.minAfternoon, 0)}
                    max={atTime(calSettings.maxAfternoon, 0)}
                    showAllDaySlot={false}
                    daysOfWeek={[1, 2, 3, 4, 5]}
                    culture='es'
                    messages={{
                        today: 'Hoy',
                        previous: 'Atrás',
                        next: 'Siguiente',
                    }}
                    formats={{
                        timeGutterFormat: 'HH:mm',
                        dayHeaderFormat: (date, culture, localizer) => {
                            const day = localizer.format(date, 'EEEE', culture); // lunes
                            const dayNum = localizer.format(date, 'd', culture); // 15
                            const month = localizer.format(date, 'LLLL', culture); // noviembre (full)
                            return `${day.charAt(0).toUpperCase() + day.slice(1)} ${dayNum} ${month}`;
                        },
                        dayRangeHeaderFormat: ({ start, end }, culture, localizer) => {
                            const s = localizer.format(start, "d 'de' LLLL", culture);
                            const e = localizer.format(end, "d 'de' LLLL", culture);
                            return `Semana del ${s} al ${e}`;
                        },
                    }}
                />
            </section>

            {modalOpenMorning && slotInfoMorning && <ReserveModal open={modalOpenMorning} onClose={handleCloseMorning} onCreated={fetchEvents} slotInfo={slotInfoMorning} />}
            {modalOpenAfter && slotInfoAfter && <ReserveModal open={modalOpenAfter} onClose={handleCloseAfter} onCreated={fetchEvents} slotInfo={slotInfoAfter} />}
        </>
    );
}
